<template>
	<div
		v-if="load"
		:id="wrapperId"
		ref="slotElement"
		:class="{
			'd-flex': true,
			'flex-wrap': buttonWidth >= windowWidth,
			'justify-end': display.mdAndUp.value,
		}"
		style="gap: 10px"
	>
		<slot></slot>
		<v-menu
			v-if="buttons.length"
			v-model="menu"
			:offset="[0, 10]"
			close-on-content-click
		>
			<template #activator="{ props: menuProps }">
				<ButtonIcon
					v-if="display[breakpoint].value && buttons.length > maxButtons"
					v-bind="menuProps"
					color="info"
					icon="fas fa-tasks"
					text="Aktionen"
				/>
			</template>
			<v-list density="compact">
				<v-list-item
					v-for="(button, index) in buttons"
					:key="index"
					:disabled="button.disabled"
					:title="translate(button.int_text)"
					:prepend-icon="translate(button?.int_icon)"
					:append-icon="translate(button?.iconRight)"
					:to="button.to ? button.to : undefined"
					@click="handleClick(button, $event)"
				>
					<template #prepend>
						<v-icon :color="button.int_color"> </v-icon>
					</template>
				</v-list-item>
			</v-list>
		</v-menu>
	</div>
</template>

<script lang="ts" setup>
import {
	ref,
	computed,
	watch,
	nextTick,
	useSlots,
	onBeforeUnmount,
	type VNode,
} from 'vue'
import { useDisplay, type DisplayBreakpoint } from 'vuetify'
import ButtonIcon from './ButtonIcon.vue'
import { uuid } from 'vue-uuid'
import {
	type ButtonTypeMapping,
	typeMapping,
} from '@/constants/buttonIconTypes.ts'
import { useTranslate } from '@/composables/useTranslate'
import { isEqual } from 'lodash'

interface ButtonData {
	int_icon: string
	int_text: string
	int_color?: string
	iconRight?: string
	click?: (event: Event) => void
	to?: string | object
	disabled?: boolean
}

interface ButtonWrapperProps {
	load: boolean
	breakpoint?: DisplayBreakpoint | 'smAndDown' | 'mdAndDown'
	multiline?: boolean
	maxButtons?: number
}

const props = withDefaults(defineProps<ButtonWrapperProps>(), {
	breakpoint: 'xs',
	multiline: false,
	maxButtons: 1,
})

const { translate } = useTranslate()
const display = useDisplay()
const slots = useSlots()

const buttonWidth = ref(0)
const buttons = ref<ButtonData[]>([])
const menu = ref(false)
const wrapperId = `button-wrapper__${uuid.v4()}`
const observer = ref<MutationObserver | null>(null)
const slotElement = ref(null)

const windowWidth = computed(() => {
	return window?.innerWidth || 1920
})

const getComponentProps = (vnode: VNode): any => {
	return vnode.props || {}
}

const isButtonComponent = (vnode: VNode): boolean => {
	if (!vnode.type) return false

	const componentName =
		typeof vnode.type === 'object' && '__name' in vnode.type
			? vnode.type.__name
			: ''

	return (
		componentName === 'ButtonIcon' ||
		componentName === 'VBtn' ||
		(typeof vnode.type === 'string' && vnode.type === 'v-btn')
	)
}

const isTooltipComponent = (vnode: VNode): boolean => {
	if (!vnode.type) return false

	const componentName =
		typeof vnode.type === 'object' && 'name' in vnode.type
			? (vnode.type as any).name
			: ''

	return (
		componentName === 'VTooltip' ||
		(typeof vnode.type === 'string' && vnode.type === 'v-tooltip')
	)
}

const extractButtonData = (
	vnode: VNode,
	isTooltip = false
): ButtonData | null => {
	const props = getComponentProps(vnode)

	// For tooltip components, we need to look at the child button
	if (isTooltip && vnode.children) {
		// Find the button inside the tooltip
		const buttonVNode = Array.isArray(vnode.children)
			? vnode.children.find(
					(child: any) =>
						typeof child === 'object' && child.type && isButtonComponent(child)
				)
			: null

		if (buttonVNode && typeof buttonVNode === 'object') {
			return extractButtonData(buttonVNode as VNode)
		}
	}

	const mappedProps: ButtonTypeMapping = props.type
		? typeMapping[props.type]
		: {
				icon: 'fal fa-question',
				text: 'Button',
				color: 'primary',
			}

	const int_icon = props.icon || props.int_icon || mappedProps.icon
	const int_text = props.text || props.int_text || mappedProps.text
	const int_color = props.color || props.int_color || mappedProps.color
	const iconRight = props.iconRight || props.iconRight
	const click = props.onClick
	const to = props.to
	const disabled = props.disabled

	return {
		int_icon,
		int_text,
		int_color,
		iconRight,
		click,
		to,
		disabled,
	}
}

const hideButtonIfNeeded = (
	element: HTMLElement,
	shouldHide: boolean
): void => {
	if (shouldHide) {
		element.style.display = 'none'
	} else {
		element.style.display = 'inline-flex'
	}
}

const slotContentRef = computed(() => slots.default?.())
watch(
	slotContentRef,
	(val, old) => {
		if (!isEqual(val, old)) {
			nextTick(() => {
				build()
			})
		}
	},
	{ immediate: true, deep: true }
)
const build = (): void => {
	buttons.value = []
	buttonWidth.value = 0

	if (!slots.default) return

	if (!slotContentRef.value || !Array.isArray(slotContentRef.value)) return

	let i = 0

	for (const vnode of slotContentRef.value) {
		// Skip comment nodes or falsy nodes
		if (!vnode || !vnode.type) continue

		let buttonData: ButtonData | null = null
		let isTooltip = false

		// Check if it's a tooltip component
		if (isTooltipComponent(vnode)) {
			isTooltip = true
			buttonData = extractButtonData(vnode, true)
		} else if (isButtonComponent(vnode)) {
			buttonData = extractButtonData(vnode)
		}

		if (!buttonData) continue // skip non-button nodes

		// Now safe to use i
		const wrapperElement = document.getElementById(wrapperId)
		if (!wrapperElement) return

		// Find button elements within the wrapper
		const buttonElements = wrapperElement.querySelectorAll('.v-btn')
		const currentButtonElement = buttonElements[i] as HTMLElement
		if (!currentButtonElement) return

		if (props.multiline && display.smAndDown.value) {
			buttonWidth.value += currentButtonElement.offsetWidth + 12
			currentButtonElement.style.marginBottom = '6px'
			return
		}

		// Handle responsive hiding
		const shouldHide =
			display[props.breakpoint].value &&
			slotContentRef.value.length > props.maxButtons &&
			getComponentProps(vnode).wrapper !== false

		hideButtonIfNeeded(currentButtonElement, shouldHide)

		// Add to buttons array
		if (getComponentProps(vnode).wrapper !== false) {
			buttons.value.push(buttonData)
		}

		i++ // increment here, **only for actual button nodes**
	}
}

const handleClick = (button, event): void => {
	button.click ? button.click(event) : undefined
	menu.value = false
}

onBeforeUnmount(() => {
	if (observer.value) {
		observer.value.disconnect()
	}
})

watch(
	() => display.name.value,
	() => {
		nextTick(() => {
			build()
		})
	}
)

watch(
	() => props.load,
	(value) => {
		if (!value) return

		nextTick(() => {
			if (!props.multiline) {
				build()
				return
			}

			build()
			observer.value = new MutationObserver(build)

			observer.value.observe(slotElement.value, {
				childList: true,
				subtree: true,
			})
		})
	},
	{ immediate: true }
)
</script>

<style lang="scss" scoped>
.v-card__actions {
	flex-grow: inherit;
}
</style>
